import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";

export interface ProjectListItem {
  id: string; // unique key e.g. `${segmentId}-${tierKey}` or `service-${slug}`
  type: "plan" | "service" | "addon";
  segment?: string; // industry segment label
  name: string; // tier or service name
  tagline?: string;
  priceLabel: string; // human price ("₹50,000", "On Request")
  priceValue: number; // numeric estimate (0 if custom)
  cadence?: string;
  features?: string[];
}

interface Ctx {
  items: ProjectListItem[];
  add: (i: ProjectListItem) => void;
  remove: (id: string) => void;
  clear: () => void;
  has: (id: string) => boolean;
  count: number;
  total: number;
}

const ProjectListContext = createContext<Ctx | undefined>(undefined);

const STORAGE_KEY = "fxcr7_project_list_v1";

export function ProjectListProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ProjectListItem[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const add = useCallback((i: ProjectListItem) => {
    setItems((prev) => (prev.some((p) => p.id === i.id) ? prev : [...prev, i]));
  }, []);
  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);
  const clear = useCallback(() => setItems([]), []);
  const has = useCallback((id: string) => items.some((p) => p.id === id), [items]);

  const total = items.reduce((s, i) => s + (i.priceValue || 0), 0);

  return (
    <ProjectListContext.Provider value={{ items, add, remove, clear, has, count: items.length, total }}>
      {children}
    </ProjectListContext.Provider>
  );
}

export function useProjectList() {
  const ctx = useContext(ProjectListContext);
  if (!ctx) throw new Error("useProjectList must be used within ProjectListProvider");
  return ctx;
}
